// Chart › Timeline, below the lanes: a trend card for every test with an H, L or critical result in the selected
// range (Andrew's ruling (a)), and "Compare results" for 1–2 of them. Mounted by care-model's ChartTimeline
// (chart-timeline.tsx), which owns the `chartTimeline` switch: this component reads no switch itself.
// Data: the Labs tab's own reads (useLabResultGroups) for the flagged rows, and the per-test history
// (GET /api/Patients/{id}/LabObservations) for each line. No result is made up; none flagged = one quiet line.
import { useMemo, useState } from 'react';
import { Button } from '@scripsteam/scrips-signal-ds';
import { flaggedTestCards, type TimelineRange } from '@/patients/lib/lab-trend-cards';
import { ChartSectionHeader } from './chart-section-header';
import { useLabResultGroups } from './labs/use-lab-result-groups';
import { LabTrendCard } from './lab-trends/lab-trend-card';
import { LabComparePanel } from './lab-trends/lab-compare-panel';

/** At most this many tests in Compare results. */
export const MAX_COMPARE = 2;

export interface LabTrendSectionProps {
  /** The chart's patient GUID. */
  patientId: string;
  /** ISO dates, both inclusive; `from` null = all time. */
  range: TimelineRange;
}

export function LabTrendSection({ patientId, range }: LabTrendSectionProps) {
  const { available, groups, externalGroups, labsByRoot, failed } = useLabResultGroups(patientId);
  const cards = useMemo(
    () => flaggedTestCards([...groups, ...externalGroups], range),
    [groups, externalGroups, range],
  );
  const [comparing, setComparing] = useState(false);
  const [picked, setPicked] = useState<string[]>([]);

  // Only cards still on screen stay picked (the range changed, or a result was withdrawn).
  const pickedCards = cards.filter((c) => picked.includes(c.key));
  const toggle = (key: string, on: boolean) =>
    setPicked((prev) => (on ? [...prev.filter((k) => k !== key), key].slice(-MAX_COMPARE) : prev.filter((k) => k !== key)));

  // Before the results store answers, render nothing: the Timeline lanes above say what is there.
  if (!available && failed.length === 0) return null;

  return (
    <section data-testid="lab-trend-section" aria-label="Lab trends" className="flex flex-col gap-3">
      <ChartSectionHeader
        title="Lab trends"
        count={cards.length}
        showAdd={false}
        extra={
          cards.length > 0 ? (
            <Button
              variant="secondary"
              size="sm"
              aria-pressed={comparing}
              onClick={() => {
                setComparing((on) => !on);
                setPicked([]);
              }}
            >
              {comparing ? 'Done comparing' : 'Compare results'}
            </Button>
          ) : undefined
        }
      />

      {failed.length > 0 ? (
        <p role="alert" className="text-xs text-status-error">
          Some lab results could not be loaded, so this list may be incomplete.
        </p>
      ) : null}

      {cards.length === 0 ? (
        <p data-testid="lab-trend-empty" className="text-sm text-text-hint">
          No high, low or critical lab results in this period.
        </p>
      ) : (
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-3">
          {cards.map((card) => (
            <LabTrendCard
              key={card.key}
              patientId={patientId}
              card={card}
              labsByRoot={labsByRoot}
              range={range}
              comparing={comparing}
              selected={picked.includes(card.key)}
              canSelect={pickedCards.length < MAX_COMPARE}
              onSelectedChange={(on) => toggle(card.key, on)}
            />
          ))}
        </div>
      )}

      {comparing && pickedCards.length > 0 ? (
        <LabComparePanel patientId={patientId} cards={pickedCards} labsByRoot={labsByRoot} range={range} />
      ) : comparing && cards.length > 0 ? (
        <p className="text-xs text-text-muted">Pick one or two tests to compare.</p>
      ) : null}
    </section>
  );
}
